/* =====================================================================================
   Data currency: what needs rechecking, and changes awaiting a decision.

   The Atlas is built on public data that moves. Registry volumes, waiting lists, centre
   counts, reimbursement routes and payer structures all decay, and nothing previously
   re-examined any of them. A market model does not break when it goes stale, it simply stops
   being true, and gets quoted in a board paper a year later.

   Two things here, matching the two tables in migration 0029:

     due()      topics past their recheck interval, four months by default
     queue()    proposed changes waiting to be approved, referred or refused

   Nothing an automated recheck produces writes to the Atlas directly. It arrives as a
   proposal carrying the current value, the proposed value and the evidence, and a person
   decides. That is what keeps every number in the model attributable to someone.
   ===================================================================================== */

import { BACKEND } from './db';
import { TENANT } from '@tenant';

export interface DueTopic {
  id: string;
  topic: string;
  scope: string | null;
  source_name: string;
  source_url: string | null;
  last_verified_on: string;
  recheck_months: number;
  next_due: string;
  overdue: boolean;
  days_overdue: number;
  owner_name: string | null;
  pending_reviews: number;
}

export interface ReviewItem {
  out_id: string;
  out_label: string | null;
  out_target: string;
  out_current_value: string | null;
  out_proposed_value: string | null;
  out_rationale: string;
  out_source_name: string | null;
  out_source_url: string | null;
  out_confidence: string | null;
  out_origin: string;
  out_status: string;
  out_referred_to: string | null;
  out_created_at: string;
}

const live = () => BACKEND === 'supabase';

function tidy(message: string): string {
  return message.replace(/^.*?(?:ERROR|error):\s*/i, '').trim() || message;
}

export async function due(): Promise<DueTopic[]> {
  if (!live()) return TENANT.seed.dataDue as DueTopic[];
  const { supabase } = await import('./supabase');
  const { data, error } = await supabase()
    .schema('app').from('v_data_due').select('*').order('next_due');
  if (error) throw new Error(tidy(error.message));
  return (data ?? []) as DueTopic[];
}

export async function queue(includeReferred = true): Promise<ReviewItem[]> {
  if (!live()) {
    const rows = TENANT.seed.reviewQueue as ReviewItem[];
    return includeReferred ? rows : rows.filter((r) => r.out_status !== 'referred');
  }
  const { supabase } = await import('./supabase');
  const { data, error } = await supabase()
    .schema('app').rpc('review_queue', { p_include_referred: includeReferred });
  if (error) throw new Error(tidy(error.message));
  return (data ?? []) as ReviewItem[];
}

/**
 * Approve, refer or reject.
 *
 * Approving is the only path that writes to the Atlas. Referring hands the question on
 * without deciding it, which is the realistic third option: many of these need the person who
 * owns the market rather than whoever is clearing the queue. Refusing requires a reason, or
 * the same proposal simply returns at the next recheck.
 */
export async function decide(
  reviewId: string,
  decision: 'approved' | 'rejected' | 'referred',
  note?: string,
  referTo?: string,
): Promise<string> {
  const { supabase } = await import('./supabase');
  const { data, error } = await supabase().schema('app').rpc('decide_change', {
    p_review_id: reviewId,
    p_decision: decision,
    p_note: note ?? null,
    p_refer_to: referTo ?? null,
  });
  if (error) throw new Error(tidy(error.message));
  return data as string;
}

/** Confirming a figure is unchanged is also a decision, and the common one. Without it the
    only way to clear an overdue topic would be to invent a change to approve. */
export async function confirmCurrent(currencyId: string, note?: string): Promise<string> {
  const { supabase } = await import('./supabase');
  const { data, error } = await supabase().schema('app').rpc('confirm_still_current', {
    p_currency_id: currencyId, p_note: note ?? null,
  });
  if (error) throw new Error(tidy(error.message));
  return data as string;
}
